//@flow
import * as Immutable from 'immutable';

import { OutletLocation } from 'common/models/flow/OutletLocation';
import type { OutletLocationModelType } from 'common/models/flow/OutletLocation';
import { TeamId } from './TeamId';
import I18nHelper from 'common/helpers/I18nHelper';
import { LatLng } from './LatLng';
import { GeocodingMetadata } from './GeocodingMetadata';
import { OutletLocationAddress } from './OutletLocationAddress';
import StringUtils from 'common/utils/StringUtils';
import { OutletLocationTypeEnum } from 'common/models/internal/MasterOutlet';

//Validation stuff
import {
    validatable,
    validate,
    validateAll,
    validateId,
    softValidateAll
} from 'common/utils/ValidationUtil';
import RegExUtil from 'common/utils/RegExUtil';
import DataUtils from 'common/utils/DataUtils';

const translationsNamespace = ['entityManagement'];

const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const ENTITY_NAME_ID = 'outletLocation';

@validatable
class InternalOutletLocation extends OutletLocation {
    static fromJS(json: OutletLocationModelType): InternalOutletLocation {
        const state: Object = Object.assign({}, json);
        state.customProperties = Immutable.Map(state.customProperties);
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.address = state.address
            ? OutletLocationAddress.fromJS(state.address)
            : state.address;
        state.latLng = state.latLng
            ? LatLng.fromJS(state.latLng)
            : state.latLng;
        state.geocodingMetadata = state.geocodingMetadata
            ? GeocodingMetadata.fromJS(state.geocodingMetadata)
            : state.geocodingMetadata;
        return new this(Immutable.Map(state));
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    get address(): ?OutletLocationAddress {
        return this._state.get('address');
    }

    get latLng(): ?LatLng {
        return this._state.get('latLng');
    }

    get geocodingMetadata(): ?GeocodingMetadata {
        return this._state.get('geocodingMetadata');
    }

    isVerified(): boolean {
        return this.geocodingMetadata && this.geocodingMetadata.verified;
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { address, latLng } = this;
        if (address && address.hasValidationErrors()) {
            return true;
        }
        if (latLng && latLng.hasValidationErrors()) {
            return true;
        }
        return this.validateId() || this.validateName();
    }

    @validateAll
    setAllValidationErrors(): this {
        let { address, latLng } = this;
        if (address) {
            address = address.setAllValidationErrors();
        } else {
            //validation when address completely is empty
            address = OutletLocationAddress.fromJS({}).setAllValidationErrors();
        }
        if (latLng) {
            latLng = latLng.setAllValidationErrors();
        }
        return this.setAddress(address).setLatLng(latLng);
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: ?string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (StringUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        const trimmedCode = code.trim();
        if (trimmedCode.length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (trimmedCode.length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (StringUtils.isEmpty(this.name?.trim())) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    @validate
    setType(type: string): this {
        return super.setType(type);
    }

    validateType() {
        if (!this.type) {
            return nsTranslate('entityValidationErrors.missingLocationType');
        }

        const outletlocationTypeValues = OutletLocationTypeEnum.values();
        if (
            !this.type?.trim() ||
            !outletlocationTypeValues.includes(this.type)
        ) {
            return nsTranslate('entityValidationErrors.typeEnumValidation', {
                typeList: outletlocationTypeValues.join(', '),
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    static defaultInit({ props }): InternalOutletLocation {
        const address = OutletLocationAddress.fromJS({
            countryCode: props.defaultCountryCode
        });
        const latLng = LatLng.defaultInit({ props });

        return InternalOutletLocation.fromJS({
            locationType: 'CLIENT'
        })
            .setAddress(address)
            .setLatLng(latLng);
    }

    sanitize({ latLng = true }: Object = {}): this {
        const { latLng: latLngValue } = this;
        let updatedOutletLocation = this;
        if (latLng && latLngValue) {
            const sanitizedLatLng = latLngValue.defaultSanitize();
            updatedOutletLocation = updatedOutletLocation.setLatLng(
                sanitizedLatLng
            );
        }

        return updatedOutletLocation;
    }

    defaultSanitize(): ?this {
        let sanitizedLocation = this.sanitize();
        sanitizedLocation = super.defaultSanitize(sanitizedLocation);

        if (sanitizedLocation) {
            const sanitizedLatLng =
                sanitizedLocation.latLng &&
                sanitizedLocation.latLng.defaultSanitize();
            return sanitizedLocation.setLatLng(sanitizedLatLng);
        }
        return sanitizedLocation;
    }

    get type(): OutletLocationTypeEnum {
        return this._state.get('type');
    }
}

export { InternalOutletLocation as OutletLocation };
